/*
 * Re-renders the cart UI after a script changes the cart behind the theme's back.
 *
 * The free-gift and upsell scripts write to /cart/*.js directly, which updates
 * the cart but leaves the drawer showing whatever Liquid rendered when the page
 * loaded. That is why the free 10ml only appeared after a manual refresh: the
 * cart was right, the screen was stale.
 *
 * Dispatching the theme's own event is not enough — the components re-render
 * from their own request, not from an event someone else fires. The Section
 * Rendering API is the supported way to ask Shopify for fresh markup, and it
 * depends on no theme internals, so a Horizon update cannot quietly break it.
 */
(function () {
  'use strict';

  var running = false;

  function sectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      var id = el.sectionId || el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function swap(id) {
    return fetch('/?section_id=' + encodeURIComponent(id), { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-items-component');
        if (!fresh) return;
        document.querySelectorAll('cart-items-component').forEach(function (live) {
          if ((live.sectionId || live.getAttribute('data-section-id')) !== id) return;
          /* innerHTML rather than replacing the element: the custom element
             instance stays connected, and its children re-upgrade on insert. */
          live.innerHTML = fresh.innerHTML;
        });
      })
      .catch(function () {});
  }

  /* The header bubble lives in its own section and has its own count. */
  function refreshBubble() {
    return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      var n = cart.item_count;
      document.querySelectorAll('.cart-bubble__text, .cart-bubble__text-count').forEach(function (el) {
        el.textContent = String(n);
        el.classList.toggle('hidden', n === 0);
      });
      document.querySelectorAll('.cart-bubble').forEach(function (el) {
        el.classList.toggle('cart-bubble--visible', n > 0);
      });
    }).catch(function () {});
  }

  /* Coming BACK from checkout is the case that looked broken. The browser
     restores the previous page from its back/forward cache: same DOM, scripts
     never re-run, so the cart shows whatever it showed before leaving. Three
     bottles that had since earned a free 10ml and an add-on still read as
     three lines.
     `pageshow` with persisted=true is the only signal that a page was restored
     rather than loaded, and `visibilitychange` covers returning to a
     backgrounded tab. Both re-read the cart and tell the gift and upsell
     scripts to reconsider. */
  function revalidate() {
    if (typeof window.PkCartRefresh === 'function') window.PkCartRefresh();
    document.dispatchEvent(new CustomEvent('pk:revalidate', { bubbles: true }));
  }

  /* Rather than trying to name every way a page can come back stale — back
     button, bfcache, HTTP cache, restored tab — just ask whether what is on
     screen still matches the cart, and fix it when it does not. The check is
     one small request and it cannot be fooled by which cache served the page.
     Runs after a short delay so the free-gift script has settled first,
     otherwise its own pending add looks like a mismatch. */
  function syncIfStale() {
    return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      var bubble = document.querySelector('.cart-bubble__text, .cart-bubble__text-count');
      var shown = bubble ? parseInt((bubble.textContent || '').trim(), 10) : NaN;
      var lines = document.querySelectorAll('cart-items-component .cart-items__title').length;

      var countWrong = !isNaN(shown) && shown !== cart.item_count;
      var linesWrong = lines > 0 && lines !== cart.items.length;

      if (countWrong || linesWrong) revalidate();
    }).catch(function () {});
  }

  window.addEventListener('pageshow', function () { setTimeout(syncIfStale, 900); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') setTimeout(syncIfStale, 400);
  });

  window.PkCartRefresh = function () {
    if (running) return Promise.resolve();
    running = true;
    var ids = sectionIds();
    return Promise.all(ids.map(swap).concat([refreshBubble()]))
      .then(function () {
        /* Anything else on the page that listens for the standard event still
           gets told, even though the re-render above did the real work. */
        document.dispatchEvent(new CustomEvent('shopify:cart:lines-update', {
          bubbles: true, detail: { source: 'pikutin' }
        }));
      })
      .catch(function () {})
      .then(function () { running = false; });
  };
})();
